import {
  Box,
  Button,
  Flex,
  Icon,
  Select,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import ExportButton from 'components/commonExport/ExportButton';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonSearch from 'components/commonSearchBar/CommonSearch';
import CommonTable from 'components/commonTable/CommonTable';
import DeleteModal from 'components/popUpModel/DeleteModal';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FaPlus } from 'react-icons/fa';
import { PiPencil, PiTrash } from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import useListState from 'hooks/useListState';
import { DeleteAdminUserData } from 'reactRedux/thunks/adminUserThunk/adminUserThunk';
import { ListAdminUserData } from 'reactRedux/thunks/adminUserThunk/adminUserThunk';
import {
  loginUserRole,
  PORTAL_USER_EXPORT_MATCH_TABLE_KEYS,
  sideBarRoutes,
} from 'utils/StaticData';

const AdminUserList = () => {
  const { tabletextColor } = useColorModeVariables();
  const navigate = useNavigate();
  const { params, setParam, setParams, debouncedParams, page, setPage } =
    useListState({
      defaults: {
        search: '',
        sortBy: 1,
        sortKey: 'firstName',
        limit: 10,
        role: '',
      },
      debounceKeys: ['search'],
    });
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();
  const userDeleteRef = useRef(null);
  const { isLoading, adminUserListData } = useSelector(
    (state) => state.AdminUserSlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);

  // RAT-1677 AC-3: the export is only offered to Admin and Dealer. The gate is
  // not cosmetic - the backend answers 403 for other roles, and the app treats a
  // 403 as an expired session, so an ungated button would log the user out.
  const canExport = [sideBarRoutes?.Admin, sideBarRoutes?.Dealer].includes(
    adminDetails?.role,
  );

  const dispatch = useDispatch();

  // RAT-1677 AC-2: the same search/role/sort the list is showing, so the file
  // covers exactly the rows on screen rather than an unfiltered dump.
  const exportFilters = useMemo(
    () => ({
      search: debouncedParams.search,
      role: params.role,
      sortKey: params.sortKey,
      sortBy: params.sortBy,
    }),
    [debouncedParams.search, params.role, params.sortKey, params.sortBy],
  );

  const getUserList = useCallback(() => {
    const apiParams = {
      page: page,
      limit: params.limit || 10,
      search: debouncedParams.search,
      sortKey: params.sortKey,
      sortBy: params.sortBy,
    };

    // Add role filter only if it has a value
    if (params.role) {
      apiParams.role = params.role;
    }

    dispatch(ListAdminUserData(apiParams));
  }, [
    dispatch,
    page,
    params.limit,
    debouncedParams.search,
    params.sortKey,
    params.sortBy,
    params.role,
  ]);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.firstName, {
      id: 'firstName',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          First Name
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue() ? info.getValue() : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.lastName, {
      id: 'lastName',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          Last Name
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {info.getValue() ? info.getValue() : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.email, {
      id: 'email',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Contact email
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue() ? info.getValue() : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.role, {
      id: 'role',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          Role
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {loginUserRole[info.getValue()] || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.actions, {
      id: 'actions',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          ACTION
        </Text>
      ),
      cell: (row) => (
        <Flex align="left">
          <Link
            to={`/admin/admin-users/edit/${row?.row?.original?.id}`}
            className="btn btn-light rounded-3 p-1"
          >
            <PiPencil size={20} title="Edit" style={{ marginRight: '20px' }} />
          </Link>
          {/* <Link
            to={`/admin/admin-users/details/${row?.row?.original?.id}`}
            className="btn btn-primary rounded-2 p-1"
          >
            <PiEye
              size={20}
              title="View"
              className="text-white"
              style={{ marginRight: '20px' }}
            />
          </Link> */}
          <div className="btn btn-danger rounded-3 p-1">
            <PiTrash
              size={20}
              className="text-white"
              onClick={() => {
                // setShow(true);
                onDeleteModalOpen();
                userDeleteRef.current = row?.row?.original?.id;
              }}
              title="Delete"
            />
          </div>
        </Flex>
      ),
    }),
  ];

  return (
    <>
      <Box>
        <Flex justify="space-between" mb={4} gap="20px" flexWrap="wrap">
          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.search ? 'green.500' : 'gray.400'}
            >
              Search
            </Text>
            <CommonSearch
              search={params.search}
              setSearch={(val) => setParam('search', val)}
              setPage={setPage}
            />
          </Box>
          <Flex gap="15px" align="flex-end">
            <Box>
              <Text
                fontSize="xs"
                fontWeight="600"
                mb="1"
                color={params.role ? 'green.500' : 'gray.400'}
              >
                Role
              </Text>
              <Select
                value={params.role}
                onChange={(e) => {
                  setParams({ role: e.target.value, page: 1 });
                }}
                width="200px"
                bg="white"
                borderRadius="12px"
                fontWeight="500"
              >
                <option value="">All</option>
                <option value="1">Super Admin</option>
                <option value="3">Merchant</option>
                <option value="4">Seller</option>
                <option value="5">Dealer</option>
                <option value="6">Organization</option>
              </Select>
            </Box>
            <Button
              bg="reviveOrange"
              color="white"
              borderRadius="12px"
              _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
              onClick={() => navigate('/admin/admin-users/add')}
            >
              <Icon
                as={FaPlus}
                width="15px"
                height="15px"
                marginRight="5px"
                color="inherit"
              />
              Add Portal User
            </Button>
            {canExport && (
              <ExportButton
                entity="portal-user"
                title="Export Portal Users"
                currentFilters={exportFilters}
                filteredTotal={adminUserListData?.total}
                matchTableKeys={PORTAL_USER_EXPORT_MATCH_TABLE_KEYS}
              />
            )}
          </Flex>
        </Flex>
        <CommonTable
          columns={columns}
          data={adminUserListData?.list}
          isLoading={isLoading}
          keyValue="id"
          isLink={true}
          parentLink="/admin/admin-users/details"
          rowclick={['actions']}
          getfilter={(e) => {
            setParam('sortBy', e.sortBy);
            setParam('sortKey', e.sortKey);
          }}
          defaultFilter={{ sortBy: params.sortBy, sortKey: params.sortKey }}
          isSorting={true}
          excludes={['firstName', 'lastName', 'email', 'role']}
          dynamicWidth={{
            firstName: '200px',
            lastName: '200px',
            email: '300px',
            role: '200px',
            actions: '200px',
          }}
        />

        {/* {adminUserListData?.total > 10 && ( */}
        <CommonPagination
          totalCount={adminUserListData?.total}
          activePage={page}
          pageCount={Math.ceil(
            adminUserListData?.total / adminUserListData?.limit,
          )}
          onPageChange={setPage}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
        {/* )} */}

        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={onDeleteModalClose}
          title="Are you sure you want to delete this user?"
          SolidBTNText="Delete"
          onDelete={() => {
            dispatch(
              DeleteAdminUserData(userDeleteRef.current, {}, () => {
                if (page > 1 && adminUserListData?.list?.length === 1) {
                  setPage(page - 1);
                } else {
                  getUserList();
                }

                onDeleteModalClose();
              }),
            );
          }}
          loader={isLoading}
        />
      </Box>
    </>
  );
};

export default AdminUserList;
